/**
 * Brand Color Engine
 *
 * Pure utility module for color manipulation and mapping brand colors
 * onto template elements. No React or DOM dependencies.
 */

import type {
  TemplateElement,
  ShapeElement,
  TextElement,
  GradientStop,
} from '@agency-smm/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BrandColorPalette {
  primary: string;       // brandColors[0]
  secondary: string;     // brandColors[1] or derived
  accent: string;        // brandColors[2] or derived
  text: string;          // auto: white or dark based on primary contrast
  textSecondary: string; // auto: lighter/darker text for secondary areas
}

// ---------------------------------------------------------------------------
// Color conversion helpers
// ---------------------------------------------------------------------------

/**
 * Convert a hex color (#RGB or #RRGGBB) to HSL.
 * Returns [h, s, l] where h is 0-360, s and l are 0-100.
 */
export function hexToHsl(hex: string): [number, number, number] {
  let r: number;
  let g: number;
  let b: number;

  // Strip leading #
  const h = hex.replace(/^#/, '');

  if (h.length === 3) {
    r = parseInt(h[0] + h[0], 16) / 255;
    g = parseInt(h[1] + h[1], 16) / 255;
    b = parseInt(h[2] + h[2], 16) / 255;
  } else {
    r = parseInt(h.substring(0, 2), 16) / 255;
    g = parseInt(h.substring(2, 4), 16) / 255;
    b = parseInt(h.substring(4, 6), 16) / 255;
  }

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    // Achromatic
    return [0, 0, l * 100];
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let hue: number;
  if (max === r) {
    hue = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  } else if (max === g) {
    hue = ((b - r) / d + 2) / 6;
  } else {
    hue = ((r - g) / d + 4) / 6;
  }

  return [hue * 360, s * 100, l * 100];
}

/**
 * Convert HSL values back to a hex color string.
 * h: 0-360, s: 0-100, l: 0-100.
 */
export function hslToHex(h: number, s: number, l: number): string {
  const sN = s / 100;
  const lN = l / 100;

  const c = (1 - Math.abs(2 * lN - 1)) * sN;
  const hh = h / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const m = lN - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;

  if (hh >= 0 && hh < 1) {
    r = c; g = x; b = 0;
  } else if (hh >= 1 && hh < 2) {
    r = x; g = c; b = 0;
  } else if (hh >= 2 && hh < 3) {
    r = 0; g = c; b = x;
  } else if (hh >= 3 && hh < 4) {
    r = 0; g = x; b = c;
  } else if (hh >= 4 && hh < 5) {
    r = x; g = 0; b = c;
  } else {
    r = c; g = 0; b = x;
  }

  const toHex = (n: number): string => {
    const val = Math.round((n + m) * 255);
    const clamped = Math.max(0, Math.min(255, val));
    return clamped.toString(16).padStart(2, '0');
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// ---------------------------------------------------------------------------
// Luminance & contrast
// ---------------------------------------------------------------------------

/**
 * Calculate relative luminance (0-1) per WCAG 2.0 spec.
 * Accepts hex colors (#RGB or #RRGGBB).
 */
export function luminance(hex: string): number {
  const h = hex.replace(/^#/, '');

  let r: number;
  let g: number;
  let b: number;

  if (h.length === 3) {
    r = parseInt(h[0] + h[0], 16) / 255;
    g = parseInt(h[1] + h[1], 16) / 255;
    b = parseInt(h[2] + h[2], 16) / 255;
  } else {
    r = parseInt(h.substring(0, 2), 16) / 255;
    g = parseInt(h.substring(2, 4), 16) / 255;
    b = parseInt(h.substring(4, 6), 16) / 255;
  }

  const linearize = (c: number): number =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

/**
 * Return a contrasting text color for the given background.
 * White for dark backgrounds, dark navy for light backgrounds.
 */
export function getContrastTextColor(backgroundColor: string): string {
  return luminance(backgroundColor) < 0.4 ? '#ffffff' : '#1a1a2e';
}

// ---------------------------------------------------------------------------
// Palette construction
// ---------------------------------------------------------------------------

/** Normalize a hue value to the 0-360 range. */
function normalizeHue(h: number): number {
  return ((h % 360) + 360) % 360;
}

/** Clamp a value between min and max. */
function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Build a BrandColorPalette from an array of brand hex colors.
 *
 * - 0 colors: returns null
 * - 1 color:  derives secondary (hue+30) and accent (hue-30)
 * - 2 colors: interpolates accent at hue midpoint
 * - 3+ colors: uses first three directly
 */
export function buildBrandPalette(brandColors: string[]): BrandColorPalette | null {
  if (!brandColors || brandColors.length === 0) {
    return null;
  }

  let primary: string;
  let secondary: string;
  let accent: string;

  if (brandColors.length === 1) {
    primary = brandColors[0];
    const [h, s, l] = hexToHsl(primary);

    secondary = hslToHex(
      normalizeHue(h + 30),
      clamp(s + 5, 0, 100),
      clamp(l - 5, 0, 100),
    );
    accent = hslToHex(
      normalizeHue(h - 30),
      clamp(s - 5, 0, 100),
      clamp(l + 5, 0, 100),
    );
  } else if (brandColors.length === 2) {
    primary = brandColors[0];
    secondary = brandColors[1];

    const [h1, s1, l1] = hexToHsl(primary);
    const [h2, s2, l2] = hexToHsl(secondary);

    // Interpolate hue via shortest arc
    let hDiff = h2 - h1;
    if (hDiff > 180) hDiff -= 360;
    if (hDiff < -180) hDiff += 360;
    const midHue = normalizeHue(h1 + hDiff / 2);

    accent = hslToHex(midHue, (s1 + s2) / 2, (l1 + l2) / 2);
  } else {
    primary = brandColors[0];
    secondary = brandColors[1];
    accent = brandColors[2];
  }

  return {
    primary,
    secondary,
    accent,
    text: getContrastTextColor(primary),
    textSecondary: getContrastTextColor(secondary),
  };
}

// ---------------------------------------------------------------------------
// Gradient remapping
// ---------------------------------------------------------------------------

/**
 * Remap a set of gradient stops to use the brand palette colors.
 *
 * - Stops are sorted by the luminance of their original color.
 * - Darkest original stop receives palette.primary.
 * - Lightest original stop receives palette.accent.
 * - Middle stops are interpolated between primary and accent in HSL space.
 * - Original stop positions (0-1) are preserved.
 */
export function remapGradientStops(
  original: GradientStop[],
  palette: BrandColorPalette,
): GradientStop[] {
  if (original.length === 0) return [];

  if (original.length === 1) {
    return [{ color: palette.primary, stop: original[0].stop }];
  }

  // Sort indices by luminance of the original color (darkest first)
  const indexed = original.map((gs, i) => ({ index: i, lum: luminance(gs.color) }));
  indexed.sort((a, b) => a.lum - b.lum);

  const [hP, sP, lP] = hexToHsl(palette.primary);
  const [hA, sA, lA] = hexToHsl(palette.accent);

  // Compute hue difference via shortest arc for interpolation
  let hueDiff = hA - hP;
  if (hueDiff > 180) hueDiff -= 360;
  if (hueDiff < -180) hueDiff += 360;

  const result: GradientStop[] = new Array(original.length);

  for (let rank = 0; rank < indexed.length; rank++) {
    const { index } = indexed[rank];
    const t = indexed.length === 1 ? 0 : rank / (indexed.length - 1);

    const h = normalizeHue(hP + hueDiff * t);
    const s = sP + (sA - sP) * t;
    const l = lP + (lA - lP) * t;

    result[index] = {
      color: hslToHex(h, s, l),
      stop: original[index].stop,
    };
  }

  return result;
}

// ---------------------------------------------------------------------------
// Adaptive color generation
// ---------------------------------------------------------------------------

/**
 * Generate an adaptive palette variation from a base palette using a numeric
 * seed. The primary hue is shifted deterministically, and secondary/accent
 * are re-derived maintaining the same relative relationships.
 */
export function generateAdaptiveColors(
  palette: BrandColorPalette,
  seed: number,
): BrandColorPalette {
  const [hP, sP, lP] = hexToHsl(palette.primary);
  const [hS] = hexToHsl(palette.secondary);
  const [hA] = hexToHsl(palette.accent);

  // Deterministic hue shift in range [-60, +60)
  const hueShift = (seed * 37) % 120 - 60;

  const newPrimaryH = normalizeHue(hP + hueShift);
  const newPrimaryS = clamp(sP + ((seed * 13) % 21 - 10), 0, 100); // +-10%
  const newPrimaryL = clamp(lP + ((seed * 7) % 11 - 5), 0, 100);   // +-5%

  const newPrimary = hslToHex(newPrimaryH, newPrimaryS, newPrimaryL);

  // Derive secondary and accent preserving original hue offsets from primary
  let secOffset = hS - hP;
  if (secOffset > 180) secOffset -= 360;
  if (secOffset < -180) secOffset += 360;

  let accOffset = hA - hP;
  if (accOffset > 180) accOffset -= 360;
  if (accOffset < -180) accOffset += 360;

  const [, sS, lS] = hexToHsl(palette.secondary);
  const [, sA, lA] = hexToHsl(palette.accent);

  const newSecondary = hslToHex(
    normalizeHue(newPrimaryH + secOffset),
    clamp(sS + ((seed * 13) % 21 - 10), 0, 100),
    clamp(lS + ((seed * 7) % 11 - 5), 0, 100),
  );

  const newAccent = hslToHex(
    normalizeHue(newPrimaryH + accOffset),
    clamp(sA + ((seed * 13) % 21 - 10), 0, 100),
    clamp(lA + ((seed * 7) % 11 - 5), 0, 100),
  );

  return {
    primary: newPrimary,
    secondary: newSecondary,
    accent: newAccent,
    text: getContrastTextColor(newPrimary),
    textSecondary: getContrastTextColor(newSecondary),
  };
}

// ---------------------------------------------------------------------------
// Element palette application
// ---------------------------------------------------------------------------

/**
 * Parse an rgba() string and return its alpha value, or null if not rgba.
 */
function parseRgbaAlpha(color: string): number | null {
  const match = color.match(
    /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*([\d.]+)\s*)?\)$/,
  );
  if (!match) return null;
  // rgb() without alpha defaults to 1
  return match[1] !== undefined ? parseFloat(match[1]) : 1;
}

/**
 * Return true if the color string is semi-transparent (alpha < 0.8).
 * Hex colors are always considered opaque.
 */
function isSemiTransparent(color: string): boolean {
  if (color.startsWith('#')) return false;
  const alpha = parseRgbaAlpha(color);
  return alpha !== null && alpha < 0.8;
}

/**
 * Apply a brand color palette to a single template element.
 *
 * - Shape with gradient: remap gradient stops.
 * - Shape with solid opaque background: replace with palette.primary.
 * - Shape that is semi-transparent: leave as-is (overlay element).
 * - Text element: set textColor to palette.text (or palette.textSecondary
 *   for tagline/quote dynamic types).
 * - All other element types: return unchanged.
 *
 * Always returns a new object (never mutates the original).
 */
export function applyPaletteToElement(
  element: TemplateElement,
  palette: BrandColorPalette,
): TemplateElement {
  if (element.elementType === 'shape') {
    const shape = element as ShapeElement;

    // Shape with active gradient
    if (
      shape.gradientType &&
      shape.gradientType !== 'none' &&
      shape.gradientColors &&
      shape.gradientColors.length > 0
    ) {
      return {
        ...shape,
        gradientColors: remapGradientStops(shape.gradientColors, palette),
      };
    }

    // Semi-transparent shape (overlay) - leave as-is
    if (isSemiTransparent(shape.backgroundColor)) {
      return { ...shape };
    }

    // Solid opaque shape - apply primary color
    return {
      ...shape,
      backgroundColor: palette.primary,
    };
  }

  if (element.elementType === 'text') {
    const text = element as TextElement;

    // Use secondary text color for tagline and quote dynamic types
    const useSecondary =
      text.isDynamic &&
      (text.dynamicType === 'tagline' || text.dynamicType === 'quote');

    return {
      ...text,
      textColor: useSecondary ? palette.textSecondary : palette.text,
    };
  }

  // Image elements and any other types - return as-is (shallow copy)
  return { ...element };
}
